import type { Match } from "../lib/types";

export function money(price: number | null | undefined, currency: string | null): string {
  if (price === null || price === undefined) return "no price";
  return `${currency ?? ""}${price.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

export function ago(iso: string | null): string {
  if (!iso) return "--";
  const seconds = (Date.now() - new Date(iso).getTime()) / 1000;
  if (seconds < 60) return `${Math.max(0, Math.round(seconds))}s ago`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h ago`;
  return `${Math.round(seconds / 86400)}d ago`;
}

export function Attributes({ attributes }: { attributes: Record<string, unknown> }) {
  const entries = Object.entries(attributes ?? {});
  if (!entries.length) return null;
  return (
    <div className="attrs">
      {entries.map(([key, value]) => (
        <span className="pill" key={key}>
          {key}: {String(value)}
        </span>
      ))}
    </div>
  );
}

export default function MatchCard({
  match,
  fresh,
  onAck,
}: {
  match: Match;
  fresh?: boolean;
  onAck?: (verdictId: number, acked: boolean) => void;
}) {
  const acked = Boolean(match.acked_at);
  return (
    <article className={`match${fresh ? " new" : ""}`}>
      {match.image_url ? (
        <img src={match.image_url} alt="" loading="lazy" />
      ) : (
        <div className="match-noimg" style={{ width: 84, height: 84 }} />
      )}
      <div>
        <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
          <a className="title" href={match.url ?? "#"} target="_blank" rel="noreferrer">
            {match.title}
          </a>
          <span className="price">{money(match.price, match.currency)}</span>
        </div>

        <div className="row" style={{ marginTop: 6, gap: 6 }}>
          <span className="pill accent">{match.watch_name}</span>
          <span className={`pill ${confidenceClass(match.confidence)}`}>
            {Math.round((match.confidence ?? 0) * 100)}%
          </span>
          {match.used_full_desc && <span className="pill">read description</span>}
          {match.condition_name && <span className="pill">{match.condition_name}</span>}
          <span className="faint">{ago(match.item_start_date ?? match.created_at)}</span>
        </div>

        <p className="reason">{match.reason}</p>
        <Attributes attributes={match.attributes} />

        {onAck && (
          <div className="row" style={{ marginTop: 10 }}>
            <button className={acked ? "ghost" : ""} onClick={() => onAck(match.verdict_id, !acked)}>
              {acked ? `acked ${ago(match.acked_at)}` : "Mark seen"}
            </button>
            {(match.seller_username || match.item_location) && (
              <span className="faint">
                {[match.seller_username, match.item_location].filter(Boolean).join(" - ")}
              </span>
            )}
          </div>
        )}
      </div>
    </article>
  );
}

export function confidenceClass(confidence: number | null): string {
  if (confidence === null) return "";
  if (confidence >= 0.8) return "pass";
  if (confidence >= 0.5) return "warn";
  return "risk";
}
